import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { UserService } from 'src/app/common/sharedServices/user/user.service';
import { TestataIntervento, InterventiFilterData, CreaInterventoRequest, EsitoCreaIntevento, TestataInterventoResponse, InterventoUpdResponse, Base64DocumentodResponse } from 'src/app/common/models/Intervento';
import { InterventiSqlAdapterService } from './interventi-sql-adapter.service';
import { Subject, lastValueFrom } from 'rxjs';
import { AppUser } from 'src/app/common/models/Utente';
import { Contatto } from 'src/app/common/models/Contatto';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PdfInterventiService } from 'src/app/compilazione-intervento/services/pdf-interventi.service';
import * as moment from 'moment';
import 'moment-timezone';
import { Router } from '@angular/router';
import { SsoService } from 'src/app/common/sharedServices/sso/sso.service';
import { AuthenticationService } from 'src/app/common/sharedServices/authentication/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class TestateInterventiService {
  private readonly INS_INTERVENTI_ROUTE = '/immto_wsr_interventi_ins';
  private readonly GET_INTERVENTI_ROUTE = '/immto_wsr_interventi_get';
  private readonly UPDATE_INTERVENTI_ROUTE = '/immto_wsr_interventi_upd';
  private readonly CLOSE_DETAIL_SUB_ROUTE = '/immto_wsr_interventi_upd';
  private readonly SEND_EMAIL_RAPPORTINO_ROUTE = '/immto_wsr_sendmail';

  public reloadInterventiPage = new Subject();

  constructor(
    private http: HttpClient,
    private testateDb: InterventiSqlAdapterService,
    private userService: UserService, // Sorgente dei dati dell'utente necessari per la chiamata,
    private toastController: ToastController,
    private translate: TranslateService,
    private pdfInterventiService: PdfInterventiService,
    private router: Router,
    private SSO: SsoService,
    private authenticationService: AuthenticationService,
  ) { }

  /**
   * Scarica l'anagrafica elenco interventi da web service e
   * li ritorna
   * Come effetto collaterale sovrascrive lo stato del
   * db degli interventi
   */
  async downloadAnagraficaTestateInterventi(): Promise<TestataIntervento[]> {
    try {
      await this.testateDb.rimuoviInterventi();
      const interventiResponse = await this.fetchTestateInterventiFromServer(new InterventiFilterData());
      if (interventiResponse && interventiResponse.SDTwsrinterventi && interventiResponse.SDTwsrinterventi.length > 0) {
        this.testateDb.inserisciInterventi(interventiResponse.SDTwsrinterventi);
      }
      return interventiResponse.SDTwsrinterventi;
    } catch (error) {
      console.error(`Si e' verificato un errore facendo il DOWNLOAD degli interventi da server`, error);
      return [];
    }
  }

  /**
   * Scarica l'anagrafica elenco interventi da web service e
   * li ritorna
   */
  async fetchTestateInterventiOnline(
    filtri: InterventiFilterData,
    numeroPagina: number,
    ordineDescrescente: boolean
  ): Promise<TestataIntervento[]> {
    try {
      const interventiResponse = await this.fetchTestateInterventiFromServer(filtri, numeroPagina, ordineDescrescente);
      if (interventiResponse.st == 0)
        return interventiResponse.SDTwsrinterventi;
      else {
        console.error(`Error: , ${interventiResponse.st} - ${interventiResponse.messer}`)
        if(interventiResponse.st == 97) {
          this.showToast('Sessione scaduta. Rieffettuare il login')
          /**
           * Quando c'è un errore di autenticazione (token scaduto) mando l'utente alla pagina della login per effettuare
           * nuovamente l'accesso
           */
          await this.logoutForNoAuthentication()
        } else {
          return [];
        }
      }
    } catch (error) {
      console.error(`Si e' verificato un errore facendo il FETCH degli interventi da server`, error);
      return [];
    }
  }

  async fetchTestataInterventoOnline(filtri: InterventiFilterData): Promise<TestataIntervento> {
    try {
      const interventi = await this.fetchTestateInterventiFromServer(filtri);
      return interventi ? interventi[0] : null;
    } catch (error) {
      console.error(`Si e' verificato un errore ottenendo il DETTAGLIO dell'intervento con id ${filtri.filtroIdIntervento}`, error);
      return null;
    }
  }

  /**
   * Cerca di inviare al WS i nuovi dati della testata di intervento
   * @param intervento 
   * @returns true se l'operazione ha successo
   * @throws un errore se il server ha rifiutato la modifica o si
   * e' verificato un errore
   */
  async modificaTestataIntervento(intervento: TestataIntervento): Promise<boolean> {
    try {
      const esitoOperazione = await this.patchTestataIntervento(intervento);
      if (!esitoOperazione) {
        throw Error(`Il server ha rifiutato la modifica`);
      }
      return true;
    } catch (error) {
      console.error(`Impossibile modificare l'intervento ${intervento.idIntervento} sul server. Da sincronizzare`, error);
      throw Error(`Impossibile modificare l'intervento ${intervento.idIntervento} sul server. Da sincronizzare`);
    }
  }

  async chiudiIntervento(intervento: TestataIntervento, codiceAddetto: string): Promise<string> {
    // const copiaIntervento = JSON.parse(JSON.stringify(intervento));
    const userData = await this.userService.getDatiUtente();
    
    //Ottengo pdf del rapportino e lo salvo nella testata del'intervento
    const {idIntervento, tipoReportScelto, firmaChisuraClienteBlob: firmabase64 } = intervento
    const base64rapportino = await this.pdfInterventiService.getPdfFirmato(idIntervento, tipoReportScelto, firmabase64)

    //Cambio stato intervento per la chiusura
    intervento.codiceStatoIntervento = '30';

    const {interessatiAMail, firmaChisuraClienteBlob, ...interventoRequest } = intervento
    interventoRequest.base64 = base64rapportino;
    //Chiudo intervento
    const completeUrl = `${userData.server}${this.CLOSE_DETAIL_SUB_ROUTE}`;

    const interventi: any[] = [];
    interventi.push(interventoRequest);

    const body = {
      'aziCdazi': userData.aziCdazi,
      'codiceAddetto': codiceAddetto,
      'token': userData.token,
      'idIntervento': idIntervento,
      'SDTwsrInterventiJson': JSON.stringify(interventi)
    }

    try {
      const response = await lastValueFrom(this.http.post<InterventoUpdResponse>(completeUrl, body));
      if (response && response.st == 0){
        const esitoInvio = this.spedisciRapportino(userData, idIntervento, base64rapportino, interessatiAMail);
        if (!esitoInvio) {
          this.showToast(this.translate.instant('Operations.sendEmailError'));
          console.error("Errore durante l'invio email.")
        }
        return base64rapportino;
      } else {
        intervento.codiceStatoIntervento = '20';  //per sicurezza ricambio stato intervento
        return '';
      }
    } catch (error) {
      console.error(`Impossibile chiudere l'intervento ${idIntervento} sul server. Da sincronizzare`, error);
      throw Error(`Impossibile chiudere l'intervento ${idIntervento} sul server. Da sincronizzare`);
    }
  }

  async spedisciRapportino(userData: AppUser, idIntervento: number, base64rapportino: string, interessatiAMail: Contatto[]): Promise<boolean>{
    const completeUrl = `${userData.server}${this.SEND_EMAIL_RAPPORTINO_ROUTE}`;

    //Crea una copia dell'oggetto contatto escludendo la proprietà soloApp
    const listaContatti = interessatiAMail.map(({ soloApp, ...contatto }) => contatto); 
    const body = {
      aziCdazi: userData.aziCdazi,
      token: userData.token,
      idIntervento: idIntervento,
      progressivo: 0,
      addetto: userData.putSigla,
      rapportinoBase64: base64rapportino,
      destinatari: [],
      destinatariCC: [],
      destinatariCCn: [],
      listaContatti: listaContatti
    }
    const messerr = await lastValueFrom(this.http.post<string>(completeUrl, body));
    return messerr == '' ? true : false
  }

  async patchTestataIntervento(intervento: TestataIntervento) {
    const userData = await this.userService.getDatiUtente();
    const completeUrl = `${userData.server}${this.UPDATE_INTERVENTI_ROUTE}`;

    const interventi: TestataIntervento[] = [];
    interventi.push(intervento);

    const body = {
      'aziCdazi': userData.aziCdazi,
      'codiceAddetto': userData.putSigla,
      'token': userData.token,
      'idIntervento': intervento.idIntervento,
      'SDTwsrInterventiJson': JSON.stringify(interventi)
    }
    const response = await lastValueFrom(this.http.post<InterventoUpdResponse>(completeUrl, body));
    return response && response.st == 0;
  }

  /**
   * Scarica e ritorna tutti gli interventi selezionabili in fase
   * di compilazione degli interventi
   */
  private async fetchTestateInterventiFromServer(
    filtri: InterventiFilterData,
    numeroPagina: number = 1,
    ordineDescrescente: boolean = true): Promise<TestataInterventoResponse> {
      
    const userData = await this.userService.getDatiUtente();
    if (userData){  //verifica necessaria perchè al logout, con la cancellazione dei dati utenti, restituiva errore.
      let completeUrl = `${userData.server}${this.GET_INTERVENTI_ROUTE}`;

      const body = {
        token: userData.token,
        idIntervento: 0,  //Per InteGRa è come un campo non valorizzato
        numeroDocumento: filtri.filtroNumeroIntervento,
        statoInterventi: filtri.filtroStatoIntervento ?? "",
        ragioneSociale: filtri.filtroRagioneSociale || "",
        adtOperatore: userData.adtNome,
        codiceCommessa: filtri.filtroCodiceCommessa || "",
        pproid: 0, //Per InteGRa è come un campo non valorizzato
        ordineDecrescente: ordineDescrescente ? 0 : 1,
        numeroPaginaCorrente: numeroPagina,
        righePerPag: 1000,
        dadata: filtri.filtroDaDataInterventoIsoString || "",
        adata: filtri.filtroADataInterventoIsoString || ""	
      };
      const response = await lastValueFrom(this.http.post<TestataInterventoResponse>(completeUrl, body));
      return response;
    } else {
      return {st: 0, messer: "", SDTwsrinterventi: []}
    }
  }

  async creaNuovoIntervento(request: CreaInterventoRequest): Promise<EsitoCreaIntevento> {
    const userData = await this.userService.getDatiUtente();
    const { token, server, putSigla } = userData;

    const dataInterventoFormatted = request.dataIntervento
      ? moment(request.dataIntervento).format("YYYY-MM-DD")
      : null;

    const dataFineInterventoFormatted = request.dataFineIntervento
      ? moment(request.dataFineIntervento).format("YYYY-MM-DD")
      : null;

    const { aziCdazi, codiceAddetto, ...filteredRequest } = request;

    const updatedRequest = {
      ...filteredRequest,
      dataIntervento: dataInterventoFormatted,
      dataFineIntervento: dataFineInterventoFormatted,
    };

    const interventoString = JSON.stringify(updatedRequest);
    const body = {
      'aziCdazi': userData.aziCdazi,
      'token': token,
      'codiceAddetto': putSigla,
      'interventojs': interventoString,
    };
    const completeUrl = `${server}${this.INS_INTERVENTI_ROUTE}`;
    return lastValueFrom(this.http.post<EsitoCreaIntevento>(completeUrl, body))
  }

  emitValue() {
    this.reloadInterventiPage.next(true);
  }

  private async showToast(message: string, title?: string) {
    const toast = await this.toastController.create({
      message,
      header: title,
      color: 'danger',
      position: 'bottom',
      duration: 3000
    });
    await toast.present();
  }

  /**
   * Metodo esattamente uguale a quello del logout generale, ma fatto direttamente in testate-interventi
   */
  async logoutForNoAuthentication() {
    await this.SSO.setLogoutSSOStatus();
    await this.authenticationService.logout();
    this.router.navigateByUrl('/login');
  }
}